import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { api } from '../../services/client';
import { useTranslation } from '../../i18n';
import { IBackofficeAdmin, IAuditLog, IAdminLoginLog } from '../../types';

// --- Interfaces ---

export interface ICreateAdminData {
  username: string;
  displayName: string;
  password: string;
  permissions: string[];
  email?: string;
  mobileNumber?: string;
  countryCode?: string;
}

export interface IUpdateAdminData {
  displayName?: string;
  email?: string | null;
  mobileNumber?: string;
  countryCode?: string;
  password?: string;
  permissions?: string[];
  isActive?: boolean;
}

// --- Query Key Factory ---

export const adminKeys = {
  all: ['admins'] as const,
  lists: () => [...adminKeys.all, 'list'] as const,
  list: () => [...adminKeys.lists()] as const,
  details: () => [...adminKeys.all, 'detail'] as const,
  detail: (id: string) => [...adminKeys.details(), id] as const,
  auditLogs: (id: string) => [...adminKeys.detail(id), 'audit-logs'] as const,
  loginLogs: (id: string) => [...adminKeys.detail(id), 'login-logs'] as const,
};

// --- Query Hooks ---

export const useAdmins = () =>
  useQuery({
    queryKey: adminKeys.list(),
    queryFn: () => api.get<IBackofficeAdmin[]>('/backoffice/admins'),
  });

export const useAdminDetail = (id: string) => {
  return useQuery({
    queryKey: adminKeys.detail(id),
    queryFn: () =>
      api
        .get<IBackofficeAdmin[]>('/backoffice/admins')
        .then((list) => list.find((a) => a.id === id) ?? null),
    enabled: !!id,
  });
};

export const useAdminAuditLogs = (adminId: string) =>
  useQuery({
    queryKey: adminKeys.auditLogs(adminId),
    queryFn: () =>
      api
        .get<{
          data: IAuditLog[];
        }>(`/backoffice/audit-logs?adminId=${adminId}&limit=20`)
        .then((res) => res.data ?? []),
    enabled: !!adminId,
  });

export const useAdminLoginLogs = (adminId: string) =>
  useQuery({
    queryKey: adminKeys.loginLogs(adminId),
    queryFn: () =>
      api.get<IAdminLoginLog[]>(
        `/backoffice/admins/${adminId}/login-logs?limit=20`,
      ),
    enabled: !!adminId,
  });

// --- Mutation Hooks ---

export const useCreateAdmin = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (data: ICreateAdminData) =>
      api.post<IBackofficeAdmin>('/backoffice/admins', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.lists() });
      toast.success(t('toast.admin_created'));
    },
    onError: (error: Error) => {
      toast.error(error.message || t('toast.admin_create_failed'));
    },
  });
};

export const useUpdateAdmin = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: IUpdateAdminData }) =>
      api.patch<IBackofficeAdmin>(`/backoffice/admins/${id}`, data),
    onSuccess: () => {
      // Invalidate all admin queries to ensure both list and detail refresh
      queryClient.invalidateQueries({ queryKey: adminKeys.all });
      toast.success(t('toast.admin_updated'));
    },
    onError: (error: Error) => {
      toast.error(error.message || t('toast.admin_update_failed'));
    },
  });
};

export const useDeleteAdmin = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/backoffice/admins/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.lists() });
      toast.success(t('toast.admin_deleted'));
    },
    onError: (error: Error) => {
      toast.error(error.message || t('toast.admin_delete_failed'));
    },
  });
};

export const useTransferOwnership = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (adminId: string) =>
      api.post<void>(`/backoffice/admins/${adminId}/transfer-ownership`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.all });
      toast.success(t('toast.ownership_transferred'));
    },
    onError: (error: Error) => {
      toast.error(error.message || t('toast.ownership_transfer_failed'));
    },
  });
};
